import React, { useEffect, useState, useCallback } from 'react';
import { Card, Button, Alert, Spinner, Form, Badge, Table } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { fetchTenants, fetchTenantQuestions, saveTenantQuestions, reloadConfig } from '../../services/adminApi';
import { useAuth } from '../../services/AuthContext';

const TABS = [
    { id: 'tolerance', label: 'Risk Tolerance'    },
    { id: 'capacity',  label: 'Capacity for Loss' },
    { id: 'life',      label: 'Life Changes'      },
];

function Questions({ tenantCode }) {
    const { can } = useAuth();
    const [data, setData]                   = useState(null);
    const [tenants, setTenants]             = useState([]);
    const [selectedTenant, setSelectedTenant] = useState(tenantCode || '');
    const [activeTab, setActiveTab]         = useState('tolerance');
    const [loading, setLoading]             = useState(true);
    const [saving, setSaving]               = useState(false);
    const [dirty, setDirty]                 = useState(false);
    const [feedback, setFeedback]           = useState(null);
    const [isDefault, setIsDefault]         = useState(false);   // tenant has no row → showing shared template

    // Embedded in the Tenants detail view the tenant is fixed via the prop (no picker); standalone,
    // load the tenant list and default to the first.
    useEffect(() => {
        if (tenantCode) { setSelectedTenant(tenantCode); return; }
        fetchTenants()
            .then(d => {
                const list = d.tenants || [];
                setTenants(list);
                if (list.length) setSelectedTenant(list[0].code);
                else setLoading(false);
            })
            .catch(() => { setFeedback({ type: 'danger', msg: 'Failed to load tenants.' }); setLoading(false); });
    }, [tenantCode]);

    const load = useCallback(() => {
        if (!selectedTenant) return;
        setLoading(true);
        fetchTenantQuestions(selectedTenant)
            .then(d => { setData(d.questions || {}); setIsDefault(d.is_default || false); setDirty(false); })
            .catch(() => setFeedback({ type: 'danger', msg: 'Failed to load questions.' }))
            .finally(() => setLoading(false));
    }, [selectedTenant]);

    useEffect(() => { load(); }, [load]);

    const mark = () => { setDirty(true); setFeedback(null); };

    const setQuestionField = (section, idx, field, value) => {
        setData(prev => {
            const questions = [...prev[section].questions];
            questions[idx] = { ...questions[idx], [field]: value };
            return { ...prev, [section]: { ...prev[section], questions } };
        });
        mark();
    };

    const setOptionField = (section, qIdx, oIdx, field, value) => {
        setData(prev => {
            const questions = [...prev[section].questions];
            const options   = [...questions[qIdx].options];
            options[oIdx]   = { ...options[oIdx], [field]: value };
            questions[qIdx] = { ...questions[qIdx], options };
            return { ...prev, [section]: { ...prev[section], questions } };
        });
        mark();
    };

    const setLifeChangeField = (idx, field, value) => {
        setData(prev => {
            const options = [...prev.life_changes.options];
            options[idx]  = { ...options[idx], [field]: value };
            return { ...prev, life_changes: { ...prev.life_changes, options } };
        });
        mark();
    };

    const handleSave = async () => {
        setSaving(true);
        setFeedback(null);
        try {
            await saveTenantQuestions(selectedTenant, data);
            setDirty(false);
            setIsDefault(false);
            setFeedback({ type: 'success', msg: `Questions saved for ${selectedTenant}.` });
        } catch (e) {
            const detail = e.response?.data?.detail;
            setFeedback({ type: 'danger', msg: Array.isArray(detail) ? detail[0]?.msg : (detail || 'Save failed.') });
        } finally {
            setSaving(false);
        }
    };

    const handleReload = async () => {
        try {
            await reloadConfig();
            load();
            setFeedback({ type: 'info', msg: 'Configuration reloaded from disk.' });
        } catch {
            setFeedback({ type: 'danger', msg: 'Reload failed.' });
        }
    };

    if (loading) {
        return (
            <div className="page-content">
                <div className="text-center py-5"><Spinner animation="border" variant="primary" /></div>
            </div>
        );
    }

    const section = activeTab === 'tolerance' ? 'tolerance' : 'capacity';

    return (
        <div className="page-content">
            <div className="page-header">
                <div>
                    <h1 className="page-title">
                        Questions
                        {dirty && (
                            <Badge bg="warning" text="dark" className="ms-2"
                                style={{ fontSize: 11, verticalAlign: 'middle' }}>
                                Unsaved changes
                            </Badge>
                        )}
                    </h1>
                    <p className="text-muted mb-0" style={{ fontSize: 13 }}>
                        Per-tenant question text, help copy, and answer option labels.
                        Weights and scoring bands are managed on the Scoring page.
                    </p>
                </div>
                <div className="d-flex gap-2 align-items-center">
                    {!tenantCode && (
                        <Form.Select
                            size="sm" value={selectedTenant} disabled={saving || !tenants.length}
                            onChange={e => setSelectedTenant(e.target.value)}
                            style={{ width: 200, fontSize: 13 }}>
                            {tenants.map(t => (
                                <option key={t.code} value={t.code}>{t.name || t.code}</option>
                            ))}
                        </Form.Select>
                    )}
                    <Button variant="outline-secondary" size="sm" onClick={handleReload} disabled={saving || !can.editAdviceConfig()}
                        title={can.editAdviceConfig() ? undefined : "Requires the waive-config role"}>
                        <FontAwesomeIcon icon="rotate-right" className="me-1" />
                        Reload
                    </Button>
                    <Button variant="primary" size="sm" onClick={handleSave} disabled={saving || (!dirty && !isDefault) || !can.editAdviceConfig()}
                        title={can.editAdviceConfig() ? undefined : "Requires the waive-config role"}>
                        {saving && <Spinner size="sm" animation="border" className="me-1" />}
                        {isDefault ? 'Create for this tenant' : 'Save Changes'}
                    </Button>
                </div>
            </div>

            {feedback && (
                <Alert variant={feedback.type} className="mb-3 py-2" style={{ fontSize: 13 }}
                    dismissible onClose={() => setFeedback(null)}>
                    {feedback.msg}
                </Alert>
            )}

            {isDefault && (
                <Alert variant="info" className="mb-3 py-2" style={{ fontSize: 13 }}>
                    This tenant has no questionnaire of its own yet — you&apos;re viewing the shared
                    {' '}<strong>default template</strong>. Edit any value and save to create a
                    tenant-specific copy.
                </Alert>
            )}

            <div className="d-flex gap-1 mb-3" style={{ borderBottom: '1px solid #e2e8f0', paddingBottom: 8 }}>
                {TABS.map(t => (
                    <button key={t.id} onClick={() => setActiveTab(t.id)} style={{
                        background: activeTab === t.id ? '#f1f5f9' : 'transparent',
                        border: 'none', borderRadius: 6, padding: '7px 14px',
                        fontSize: 13, fontWeight: activeTab === t.id ? 600 : 400,
                        color: activeTab === t.id ? '#0f172a' : '#64748b', cursor: 'pointer',
                    }}>
                        {t.label}
                    </button>
                ))}
            </div>

            {data && Object.keys(data).length === 0 && (
                <Alert variant="light" className="border" style={{ fontSize: 13 }}>
                    This tenant has no questionnaire yet.
                </Alert>
            )}

            {(activeTab === 'tolerance' || activeTab === 'capacity') && data?.[section]?.questions && (
                <div className="d-flex flex-column gap-3">
                    {data[section].questions.map((q, qIdx) => (
                        <Card key={q.id} className="border-0 shadow-sm">
                            <Card.Header className="bg-transparent border-bottom py-2 px-4 d-flex align-items-center gap-2">
                                <Badge bg="secondary" style={{ fontFamily: 'monospace', fontSize: 12 }}>
                                    {q.id}
                                </Badge>
                                <span style={{ fontSize: 12, color: '#64748b' }}>
                                    Question {q.order}
                                </span>
                            </Card.Header>
                            <Card.Body className="px-4 py-3">
                                <Form.Group className="mb-3">
                                    <Form.Label style={{ fontSize: 12, color: '#64748b', marginBottom: 4 }}>
                                        Question text
                                    </Form.Label>
                                    <Form.Control
                                        as="textarea" rows={2}
                                        value={q.text}
                                        onChange={e => setQuestionField(section, qIdx, 'text', e.target.value)}
                                        style={{ fontSize: 13 }}
                                    />
                                </Form.Group>
                                <Form.Group className="mb-3">
                                    <Form.Label style={{ fontSize: 12, color: '#64748b', marginBottom: 4 }}>
                                        Help text
                                    </Form.Label>
                                    <Form.Control
                                        type="text"
                                        value={q.help_text || ''}
                                        onChange={e => setQuestionField(section, qIdx, 'help_text', e.target.value)}
                                        style={{ fontSize: 13 }}
                                    />
                                </Form.Group>
                                <div style={{ fontSize: 12, color: '#64748b', marginBottom: 6, fontWeight: 600 }}>
                                    Answer options
                                </div>
                                <Table size="sm" borderless className="mb-0" style={{ fontSize: 13 }}>
                                    <thead>
                                        <tr style={{ borderBottom: '1px solid #e2e8f0', color: '#64748b', fontSize: 12 }}>
                                            <th style={{ width: 56, fontWeight: 600 }}>Score</th>
                                            <th style={{ width: '35%', fontWeight: 600 }}>Label</th>
                                            <th style={{ fontWeight: 600 }}>Description</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {q.options.map((opt, oIdx) => (
                                            <tr key={opt.value}>
                                                <td className="align-middle">
                                                    <Badge bg="light" text="dark"
                                                        style={{ fontFamily: 'monospace', fontSize: 12, border: '1px solid #e2e8f0' }}>
                                                        {opt.score}
                                                    </Badge>
                                                </td>
                                                <td className="align-middle" style={{ paddingRight: 8 }}>
                                                    <Form.Control
                                                        type="text" size="sm"
                                                        value={opt.label}
                                                        onChange={e => setOptionField(section, qIdx, oIdx, 'label', e.target.value)}
                                                        style={{ fontSize: 13 }}
                                                    />
                                                </td>
                                                <td className="align-middle">
                                                    <Form.Control
                                                        type="text" size="sm"
                                                        value={opt.description || ''}
                                                        onChange={e => setOptionField(section, qIdx, oIdx, 'description', e.target.value)}
                                                        placeholder="—"
                                                        style={{ fontSize: 13 }}
                                                    />
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </Table>
                            </Card.Body>
                        </Card>
                    ))}
                </div>
            )}

            {activeTab === 'life' && data?.life_changes?.options && (
                <Card className="border-0 shadow-sm">
                    <Card.Header className="bg-transparent border-bottom py-2 px-4 d-flex align-items-center gap-3">
                        <span className="fw-semibold" style={{ fontSize: 13 }}>Expected Life Changes</span>
                        <span className="text-muted" style={{ fontSize: 12 }}>
                            {data.life_changes.description}
                        </span>
                    </Card.Header>
                    <Card.Body style={{ padding: '0 24px' }}>
                        <div className="d-flex py-2 px-1"
                            style={{ borderBottom: '2px solid #e2e8f0', fontSize: 12, color: '#64748b', fontWeight: 600 }}>
                            <div style={{ width: 120 }}>ID / impact</div>
                            <div style={{ flex: 1, paddingRight: 12 }}>Label</div>
                            <div style={{ flex: 2 }}>Description</div>
                        </div>
                        {data.life_changes.options.map((opt, idx) => (
                            <div key={opt.id}
                                className="d-flex align-items-center py-2 px-1"
                                style={{ borderBottom: '1px solid #f1f5f9' }}>
                                <div style={{ width: 120 }}>
                                    <div style={{ fontFamily: 'monospace', fontSize: 11, color: '#64748b' }}>
                                        {opt.id}
                                    </div>
                                    <Badge
                                        bg={opt.impact === 'high' ? 'danger' : opt.impact === 'medium' ? 'warning' : opt.impact === 'low' ? 'info' : 'secondary'}
                                        text={opt.impact === 'medium' ? 'dark' : undefined}
                                        style={{ fontSize: 10 }}>
                                        {opt.impact}
                                    </Badge>
                                </div>
                                <div style={{ flex: 1, paddingRight: 12 }}>
                                    <Form.Control
                                        type="text" size="sm"
                                        value={opt.label}
                                        onChange={e => setLifeChangeField(idx, 'label', e.target.value)}
                                        style={{ fontSize: 13 }}
                                    />
                                </div>
                                <div style={{ flex: 2 }}>
                                    <Form.Control
                                        type="text" size="sm"
                                        value={opt.description || ''}
                                        onChange={e => setLifeChangeField(idx, 'description', e.target.value)}
                                        style={{ fontSize: 13 }}
                                    />
                                </div>
                            </div>
                        ))}
                    </Card.Body>
                </Card>
            )}
        </div>
    );
}

export default Questions;
